import { FileLock2, Loader2, Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ORG_NOTE_MAX_LENGTH, type OrgNoteDraft } from "./use-org-note-draft";

interface OrgNoteDialogProps {
  orgNote: string | null;
  canEdit: boolean;
  inputId: string;
  open: boolean;
  isSaving: boolean;
  draft: OrgNoteDraft;
}

/** Inline org note text, with an edit button that opens a dialog. */
export function OrgNoteDialog({
  orgNote,
  canEdit,
  inputId,
  open,
  isSaving,
  draft,
}: OrgNoteDialogProps) {
  return (
    <>
      <div className="min-w-36 max-w-64 space-y-1.5">
        {orgNote ? (
          <p className="whitespace-pre-wrap break-words text-sm text-slate-700">
            {orgNote}
          </p>
        ) : (
          <p className="text-xs italic text-slate-400">No internal note</p>
        )}
        {canEdit && (
          <OrgNoteEditButton
            hasNote={Boolean(orgNote)}
            onClick={() => draft.handleOpenChange(true)}
          />
        )}
      </div>

      <Dialog open={open} onOpenChange={draft.handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileLock2 className="h-5 w-5 text-slate-600" />
              Org Note
            </DialogTitle>
            <DialogDescription>
              Only authorized users in this business unit can see this note. It
              is never shown to the provider.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor={inputId}>Note</Label>
            <Textarea
              id={inputId}
              value={draft.note}
              onChange={(event) => draft.setNote(event.target.value)}
              maxLength={ORG_NOTE_MAX_LENGTH}
              rows={6}
              placeholder="Add context for your business unit..."
              disabled={isSaving}
            />
            <p className="text-right text-xs text-slate-500">
              {draft.note.length.toLocaleString()} /{" "}
              {ORG_NOTE_MAX_LENGTH.toLocaleString()}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => draft.handleOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={draft.saveNote} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function OrgNoteEditButton({
  hasNote,
  onClick,
}: {
  hasNote: boolean;
  onClick: () => void;
}) {
  const Icon = hasNote ? Pencil : Plus;

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-7 px-2 text-xs text-slate-600"
      onClick={onClick}
    >
      <Icon className="mr-1 h-3.5 w-3.5" />
      {hasNote ? "Edit note" : "Add note"}
    </Button>
  );
}
